import type { ControllingPerson, KYC, RelatedPartyAMLScreening } from '../../webapp/src/types';
import { isScreenableBeneficialOwner } from './beneficialOwners';

// Sunday 25 October 2026, 12:00 noon in Kuwait (UTC+03:00). The cutoff is exclusive.
export const CONTROLLING_PERSON_CIVIL_ID_CUTOFF = Date.parse('2026-10-25T09:00:00.000Z');

function submissionTime(value: unknown): number | undefined {
    let milliseconds: unknown;
    try {
        if (value instanceof Date) milliseconds = value.getTime();
        else if (typeof value === 'string' && /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) milliseconds = Date.parse(value);
        else if (value && typeof value === 'object') {
            const timestamp = value as Record<string, unknown>;
            if (typeof timestamp.toMillis === 'function') milliseconds = timestamp.toMillis();
            else if (typeof timestamp.toDate === 'function') milliseconds = timestamp.toDate()?.getTime();
            else {
                const seconds = timestamp.seconds ?? timestamp._seconds;
                const nanos = timestamp.nanoseconds ?? timestamp._nanoseconds ?? 0;
                if (typeof seconds === 'number' && typeof nanos === 'number') milliseconds = seconds * 1000 + nanos / 1e6;
            }
        }
    } catch {
        return undefined;
    }
    return typeof milliseconds === 'number' && Number.isFinite(milliseconds) ? milliseconds : undefined;
}

/** Only a known pre-cutoff submission with a missing Civil ID is exempt. */
export function skipControllingPersonScreening(
    kyc: Pick<KYC, 'created'> | undefined,
    person: Pick<ControllingPerson, 'civilID'> | undefined,
    originalSubmittedAt?: unknown,
): boolean {
    if (!person || (person.civilID != null && (typeof person.civilID !== 'string' || person.civilID.trim() !== ''))) return false;
    // Callers holding a Firestore snapshot supply its immutable createTime.
    const submitted = submissionTime(originalSubmittedAt === undefined ? kyc?.created : originalSubmittedAt);
    return submitted !== undefined && submitted < CONTROLLING_PERSON_CIVIL_ID_CUTOFF;
}

/** Historical results for an exempt row must not create a new approval requirement. */
export function skipControllingPersonResult(kyc: KYC, related: RelatedPartyAMLScreening, originalSubmittedAt?: unknown): boolean {
    // Authorized signatory and guardian checks always retain their existing requirements.
    if (!['board_director', 'executive_manager'].includes(related.relation)) return false;
    if ((kyc.beneficiaries ?? []).some(owner => isScreenableBeneficialOwner(owner) && owner.customerID === related.customerID)) return false;
    const people = [...(kyc.board_of_directors ?? []), ...(kyc.executive_management ?? [])]
        .filter(person => person.customerID === related.customerID);
    // A person listed in another role with an ID must still be screened and reviewed.
    return people.length > 0 && people.every(person => skipControllingPersonScreening(kyc, person, originalSubmittedAt));
}
